/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

/* ===========================================================================
   FIZI CART DISCOUNT CONTROLLER
   ---------------------------------------------------------------------------
   Drives the custom cart-summary discount UI that ships in
   snippets/cart-details.liquid (markup) + snippets/fizi-redesign.liquid (CSS)
   but had no JavaScript wired to it. Without this, a discount applied via an
   email /discount/<CODE> link reduces the cart total but shows no removable
   chip/row, and the manual "apply code" field does nothing.

   What it does, on the cart page AND the cart drawer:
     1. Reads the live cart and, for every cart-level discount, renders the
        green removable summary row (.cart__discount-row[data-js-rendered])
        the theme CSS already styles — code + amount + "x".
     2. "x" removes the discount for real via POST /cart/update.js {discount:''}
        and re-renders totals (verified working on this store: total restores,
        discount_codes -> []).
     3. The manual apply field (#fizi-discount-btn / Enter) applies a typed code
        via POST /cart/update.js {discount:'<CODE>'}, then shows the chip or an
        error message.
     4. Keeps the custom subtotal / shipping / free-shipping promo / total rows
        in sync (the theme's own web components don't know about these custom
        elements).
     5. Re-renders on every AJAX cart change (subscribes to the theme cart
        store), on drawer open, and on initial load.

   Notes:
     - custom.js is a static asset (no Liquid). The free-shipping threshold is
       read from <free-shipping-bar minimum-value>; copy/labels are constants
       below (store primary locale = Spanish).
   =========================================================================== */
(function () {
  'use strict';

  var STR = {
    free: 'Gratis',
    freeApplied: '¡<strong>ENVÍO GRATIS</strong> aplicado!',
    remaining: 'Te faltan {{ amount }} para <strong>ENVÍO GRATIS</strong>',
    applied: '¡Código aplicado!',
    invalid: 'El código de descuento no es válido o no se puede aplicar',
    error: 'Error al aplicar el código',
    enterCode: 'Introduce un código de descuento',
    removeTitle: 'Eliminar descuento'
  };

  var FLAT_SHIP_CENTS = 350;          // mirrors the "envío" line in cart-details.liquid
  var FALLBACK_FREE_SHIP_CENTS = 5000; // €50 — used only if the bar attribute is absent

  var DISCOUNT_ICON =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: -1px; margin-right: 4px;">' +
    '<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';

  /* ---- helpers ----------------------------------------------------------- */

  function storeReady() {
    return window.theme && window.theme.cart && window.theme.cart.store &&
      typeof window.theme.cart.store.getState === 'function';
  }

  function getCart() {
    try {
      if (storeReady()) {
        var s = window.theme.cart.store.getState();
        if (s && s.products) return s.products;
      }
    } catch (e) {}
    return null;
  }

  function money(cents) {
    cents = Number(cents) || 0;
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' &&
        window.theme && window.theme.money_format) {
        return window.Shopify.formatMoney(cents, window.theme.money_format);
      }
    } catch (e) {}
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function freeShipThreshold() {
    var bar = document.querySelector('free-shipping-bar[minimum-value]');
    if (bar) {
      var v = parseInt(bar.getAttribute('minimum-value'), 10);
      if (!isNaN(v) && v > 0) return v;
    }
    return FALLBACK_FREE_SHIP_CENTS;
  }

  // Cart-level discount applications, normalised to {title, amount} pairs.
  // Handles both cart.js shape ({title, total_allocated_amount}) and the
  // AJAX-response shape ({discount_application:{title}, amount}).
  function cartDiscounts(cart) {
    var apps = (cart && cart.cart_level_discount_applications) || [];
    var out = [];
    for (var i = 0; i < apps.length; i++) {
      var a = apps[i] || {};
      if (a.discount_application) {
        out.push({
          title: a.discount_application.title || a.discount_application.code || '',
          amount: Number(a.amount) || 0
        });
      } else {
        out.push({
          title: a.title || a.code || '',
          amount: Number(a.total_allocated_amount) || 0
        });
      }
    }
    return out.filter(function (d) { return d.title; });
  }

  /* ---- rendering --------------------------------------------------------- */

  var _writing = false;

  function renderAll() {
    var cart = getCart();
    if (!cart) return;
    var scopes = document.querySelectorAll('.cart__order-details');
    if (!scopes.length) return;
    _writing = true;
    try {
      for (var i = 0; i < scopes.length; i++) renderScope(scopes[i], cart);
    } finally {
      // release the write-guard on the next frame so our own DOM mutations
      // don't re-trigger the observer
      window.requestAnimationFrame(function () { _writing = false; });
    }
  }

  function renderScope(scope, cart) {
    var discounts = cartDiscounts(cart);
    var subtotal = Number(cart.items_subtotal_price) || 0;
    var total = Number(cart.total_price) || 0;

    // --- discount rows (idempotent) ---
    var existing = scope.querySelectorAll('.cart__discount-row[data-js-rendered]');
    var desiredSig = discounts.map(function (d) { return d.title + '|' + d.amount; }).join('~');
    var currentSig = Array.prototype.map.call(existing, function (el) {
      return el.getAttribute('data-sig');
    }).join('~');

    if (desiredSig !== currentSig) {
      for (var e = 0; e < existing.length; e++) existing[e].parentNode.removeChild(existing[e]);
      var anchor = scope.querySelector('.cart__subtotal-row');
      var insertAfter = anchor || null;
      for (var d = 0; d < discounts.length; d++) {
        var row = buildDiscountRow(discounts[d]);
        if (insertAfter && insertAfter.parentNode === scope) {
          scope.insertBefore(row, insertAfter.nextSibling);
        } else {
          var shipRow = scope.querySelector('.cart__shipping-row');
          if (shipRow) scope.insertBefore(row, shipRow);
          else scope.insertBefore(row, scope.firstChild);
        }
        insertAfter = row;
      }
    }

    // --- subtotal --- (money_format may contain HTML, so use innerHTML)
    var subEl = scope.querySelector('.fizi-subtotal-value, #fizi-subtotal-value');
    if (subEl) setHTML(subEl, money(subtotal));

    // --- shipping + free-shipping promo ---
    var threshold = freeShipThreshold();
    var qualifies = total >= threshold;
    var shipEl = scope.querySelector('.fizi-shipping-value, #fizi-shipping-value');
    if (shipEl) {
      if (qualifies) {
        setHTML(shipEl, STR.free);
        shipEl.classList.add('fizi-shipping--free');
      } else {
        setHTML(shipEl, money(FLAT_SHIP_CENTS));
        shipEl.classList.remove('fizi-shipping--free');
      }
    }
    var promoEl = scope.querySelector('.fizi-shipping-promo, #fizi-shipping-promo');
    if (promoEl) {
      var html = qualifies
        ? STR.freeApplied
        : STR.remaining.replace('{{ amount }}', money(threshold - total));
      setHTML(promoEl, html);
    }

    // --- total (matches the theme: cart total_price, excludes shipping) ---
    var totalEl = scope.querySelector('.fizi-total-value, #fizi-total-value');
    if (totalEl) setHTML(totalEl, money(total));
  }

  function buildDiscountRow(d) {
    var p = document.createElement('p');
    p.className = 'cart__total cart__discount-row';
    p.setAttribute('data-js-rendered', '');
    p.setAttribute('data-sig', d.title + '|' + d.amount);

    var label = document.createElement('span');
    label.className = 'fizi-discount-row__label';
    label.innerHTML = DISCOUNT_ICON + ' ' + escapeHTML(d.title) + ' ';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'fizi-discount-row__remove';
    btn.setAttribute('data-discount-code', d.title);
    btn.setAttribute('title', STR.removeTitle);
    btn.textContent = '×';
    label.appendChild(btn);

    var amount = document.createElement('span');
    amount.innerHTML = '-' + money(d.amount); // money_format may contain HTML

    p.appendChild(label);
    p.appendChild(amount);
    return p;
  }

  // Idempotent setters keyed on a cached last-value so browser HTML
  // normalisation (e.g. class=x -> class="x") never causes redundant writes.
  function setText(el, txt) {
    if (el.__fiziTxt === txt) return;
    el.__fiziTxt = txt; el.textContent = txt;
  }
  function setHTML(el, html) {
    if (el.__fiziHtml === html) return;
    el.__fiziHtml = html; el.innerHTML = html;
  }
  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- cart mutations ---------------------------------------------------- */

  var _busy = false;

  function refreshStoreFromServer() {
    return fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (fresh) {
        if (storeReady()) {
          try { window.theme.cart.store.setState({ products: fresh }); } catch (e) {}
        }
        return fresh;
      });
  }

  function setApplyLoading(on) {
    document.querySelectorAll('.fizi-discount__btn').forEach(function (btn) {
      var text = btn.querySelector('.fizi-discount__btn-text');
      var load = btn.querySelector('.fizi-discount__btn-loading');
      if (text) text.style.display = on ? 'none' : '';
      if (load) load.style.display = on ? '' : 'none';
      btn.disabled = on;
    });
  }

  function showMessage(kind, text) {
    document.querySelectorAll('.fizi-discount__message').forEach(function (el) {
      el.textContent = text || '';
      el.classList.remove('fizi-discount__message--success', 'fizi-discount__message--error');
      if (text) {
        el.classList.add(kind === 'success'
          ? 'fizi-discount__message--success'
          : 'fizi-discount__message--error');
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  function clearInputs() {
    document.querySelectorAll('.fizi-discount__input').forEach(function (i) { i.value = ''; });
  }

  function applyCode(code) {
    code = (code || '').trim();
    if (!code) { showMessage('error', STR.enterCode); return; }
    if (_busy) return;
    _busy = true;
    setApplyLoading(true);
    showMessage(null, '');
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    })
      .then(function () { return refreshStoreFromServer(); })
      .then(function (cart) {
        var codes = (cart && cart.discount_codes) || [];
        var match = codes.filter(function (c) {
          return (c.code || '').toLowerCase() === code.toLowerCase();
        })[0];
        var applied = match ? match.applicable : cartDiscounts(cart).length > 0;
        if (applied) {
          showMessage('success', STR.applied);
          clearInputs();
        } else {
          showMessage('error', STR.invalid);
        }
        renderAll();
      })
      .catch(function () { showMessage('error', STR.error); })
      .then(function () { _busy = false; setApplyLoading(false); });
  }

  function removeCode() {
    if (_busy) return;
    _busy = true;
    showMessage(null, '');
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' })
    })
      .then(function () { return refreshStoreFromServer(); })
      .then(function () { renderAll(); })
      .catch(function () {})
      .then(function () { _busy = false; });
  }

  /* ---- event wiring (delegated, survives DOM re-renders) ----------------- */

  document.addEventListener('click', function (ev) {
    var rm = ev.target.closest(
      '.fizi-discount-row__remove, #fizi-discount-remove, .fizi-discount__remove'
    );
    if (rm) { ev.preventDefault(); removeCode(); return; }

    var apply = ev.target.closest('#fizi-discount-btn, .fizi-discount__btn');
    if (apply) {
      ev.preventDefault();
      var scope = apply.closest('.fizi-discount') || document;
      var input = scope.querySelector('.fizi-discount__input, #fizi-discount-input');
      applyCode(input ? input.value : '');
    }
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key !== 'Enter') return;
    var input = ev.target.closest('.fizi-discount__input, #fizi-discount-input');
    if (!input) return;
    ev.preventDefault();
    applyCode(input.value);
  });

  /* ---- re-render triggers ------------------------------------------------ */

  var _rafQueued = false;
  function scheduleRender() {
    if (_rafQueued) return;
    _rafQueued = true;
    window.requestAnimationFrame(function () {
      _rafQueued = false;
      renderAll();
    });
  }

  function subscribeStore() {
    if (!storeReady()) return false;
    try {
      window.theme.cart.store.subscribe(function () { scheduleRender(); });
      return true;
    } catch (e) { return false; }
  }

  function observeCarts() {
    if (typeof MutationObserver === 'undefined') return;
    var obs = new MutationObserver(function () {
      if (_writing) return;       // ignore our own writes
      scheduleRender();
    });
    document.querySelectorAll('interactive-cart').forEach(function (el) {
      obs.observe(el, { childList: true, subtree: true });
    });
  }

  function boot() {
    // wait for the cart store, then wire everything up
    var tries = 0;
    (function waitStore() {
      if (subscribeStore() || tries > 60) {
        observeCarts();
        renderAll();
        return;
      }
      tries++;
      setTimeout(waitStore, 100);
    })();
    // catch the initial server-rendered state regardless of store timing
    renderAll();
    setTimeout(renderAll, 300);
    setTimeout(renderAll, 1200);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
